import { useState, useEffect } from "react";
import "./EditGroupModal.scss";

type EditGroupModalProps = {
  isOpen: boolean;
  onClose: () => void;
  groupName: string;
  selectedStudents: string[];
  availableStudents: string[];
  studentsInOtherCustomGroups: string[];
  onSave: (selectedStudents: string[]) => void;
  isLoading?: boolean;
};

const EditGroupModal = ({
  isOpen,
  onClose,
  groupName,
  selectedStudents,
  availableStudents,
  studentsInOtherCustomGroups,
  onSave,
  isLoading = false,
}: EditGroupModalProps) => {
  const [checkedStudents, setCheckedStudents] = useState<string[]>([]);

  useEffect(() => {
    setCheckedStudents(selectedStudents);
  }, [selectedStudents]);

  if (!isOpen) return null;

  console.log('studentsInOtherCustomGroups:', studentsInOtherCustomGroups);
  

  const toggleStudent = (student: string) => {
    // Allow interaction if student is currently selected (part of current group)
    // Only prevent interaction if student is in another custom group AND not currently selected
    if (studentsInOtherCustomGroups.includes(student) && !checkedStudents.includes(student)) {
      return;
    }
    
    setCheckedStudents((prev) =>
      prev.includes(student)
        ? prev.filter((s) => s !== student)
        : [...prev, student]
    );
  };

  const handleSave = () => {
    onSave(checkedStudents);
    onClose();
  };

  const handleCancel = () => {
    setCheckedStudents(selectedStudents);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={handleCancel}>
      <div className="edit-group-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Edit Group - {groupName}</h2>
          <button className="close-btn" onClick={onClose}>
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
              <line
                x1="9"
                y1="9"
                x2="27"
                y2="27"
                stroke="#9A8E8E"
                strokeWidth="4"
                strokeLinecap="round"
              />
              <line
                x1="27"
                y1="9"
                x2="9"
                y2="27"
                stroke="#9A8E8E"
                strokeWidth="4"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="students-list">
          {availableStudents.map((student) => {
            // Student is disabled only if they're in another group AND their initial selection status was false
            const wasInitiallySelected = selectedStudents.includes(student);
            const isInOtherGroup = studentsInOtherCustomGroups.includes(student) && !wasInitiallySelected;
            return (
            <div
              key={student}
              className={`student-item ${
                isInOtherGroup ? "disabled" : ""
              }`}
              onClick={() => toggleStudent(student)}
            >
              <span className="student-name">
                {student}
                {isInOtherGroup && (
                  <span className="disabled-reason">(Already in custom group)</span>
                )}
              </span>
              <div className="checkbox">
                {checkedStudents.includes(student) && (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect
                      x="2"
                      y="2"
                      width="24"
                      height="24"
                      rx="4"
                      fill={isInOtherGroup ? "#c8c8c8" : "#9747FF"}
                    />
                    <path
                      d="M7.83 12.25L12.08 16.5L20.17 8.5"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
                {!checkedStudents.includes(student) && (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect
                      x="2"
                      y="2"
                      width="24"
                      height="24"
                      rx="4"
                      fill="white"
                      stroke="#E5E1E1"
                      strokeWidth="2"
                    />
                  </svg>
                )}
              </div>
            </div>
            );
          })}
        </div>

        <div className="modal-actions">
          <button className="cancel-btn" onClick={handleCancel}>
            Cancel
          </button>
          <button 
            className="save-btn" 
            onClick={handleSave}
            disabled={isLoading}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M5 12L10 17L20 7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {isLoading ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditGroupModal;
